import {
  type HTMLAttributes,
  type DetailedHTMLProps,
  type DOMAttributes,
  useEffect,
  useContext,
  useState,
  useCallback,
  useReducer,
  useMemo,
  createContext
} from 'react';
import { BrowserRouter as Router, Switch, Route, useParams } from 'react-router-dom';
import ZoomVideo, {
  type VideoPlayerContainer,
  type VideoPlayer,
  ConnectionState,
  ReconnectReason
} from '@zoom/videosdk';
import { message, Modal } from 'antd';
import 'antd/dist/antd.min.css';
import produce from 'immer';
import Home from './feature/home/home';
import Video from './feature/video/video';
import VideoSingle from './feature/video/video-single';
import VideoAttach from './feature/video/video-attach';
import Preview from './feature/preview/preview';
import Leave from './feature/leave/leave';
import ZoomContext from './context/zoom-context';
import ZoomMediaContext from './context/media-context';
import LoadingLayer from './component/loading-layer';
import Chat from './feature/chat/chat';
import Command from './feature/command/command';
import Subsession from './feature/subsession/subsession';
import type { MediaStream, Participant } from './index-types';
import './App.css';
type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>;

export enum UserRole {
  Student = '0',
  Instructor = '1',
  Csm = '2'
}

export enum ProcessorName {
  PoseDetection = 'pose-processor'
  // Watermark = 'watermark-processor'
}

// Create a context for speakerView
export const ViewContext = createContext<{
  speakerView: boolean;
  galleryView: boolean;
  mainViewUser: Participant | null | undefined;
  pinViewUser: Participant | null | undefined;
  setSpeakerView: (value: boolean) => void;
  setGalleryView: (value: boolean) => void;
  setMainViewUser: (value: Participant | null | undefined) => void;
  setPinViewUser: (value: Participant | null | undefined) => void;
}>({
  speakerView: true,
  galleryView: false,
  mainViewUser: null,
  pinViewUser: null,
  setSpeakerView: () => {},
  setGalleryView: () => {},
  setMainViewUser: () => {},
  setPinViewUser: () => {}
});

interface AppProps {
  meetingArgs: {
    sdkKey: string;
    topic: string;
    signature: string;
    name: string;
    password?: string;
    webEndpoint?: string;
    enforceGalleryView?: string;
    enforceVB?: string;
    customerJoinId?: string;
    lang?: string;
    useVideoPlayer?: string;
  };
}
const mediaShape = {
  audio: {
    encode: false,
    decode: false
  },
  video: {
    encode: false,
    decode: false
  },
  share: {
    encode: false,
    decode: false
  }
};
const mediaReducer = produce((draft, action) => {
  switch (action.type) {
    case 'audio-encode': {
      draft.audio.encode = action.payload;
      break;
    }
    case 'audio-decode': {
      draft.audio.decode = action.payload;
      break;
    }
    case 'video-encode': {
      draft.video.encode = action.payload;
      break;
    }
    case 'video-decode': {
      draft.video.decode = action.payload;
      break;
    }
    case 'share-encode': {
      draft.share.encode = action.payload;
      break;
    }
    case 'share-decode': {
      draft.share.decode = action.payload;
      break;
    }
    case 'reset-media': {
      Object.assign(draft, { ...mediaShape });
      break;
    }
    default:
      break;
  }
}, mediaShape);

declare global {
  interface Window {
    webEndpoint: string | undefined;
    zmClient: any | undefined;
    mediaStream: any | undefined;
    crossOriginIsolated: boolean;
    ltClient: any | undefined;
    logClient: any | undefined;
  }
  namespace JSX {
    interface IntrinsicElements {
      ['video-player']: DetailedHTMLProps<HTMLAttributes<VideoPlayer>, VideoPlayer> & { class?: string };
      ['video-player-container']: CustomElement<VideoPlayerContainer> & { class?: string };
    }
  }
}

function App(props: AppProps) {
  const {
    meetingArgs: {
      sdkKey,
      topic,
      signature,
      name,
      password,
      webEndpoint: webEndpointArg,
      enforceGalleryView,
      enforceVB,
      customerJoinId,
      lang,
      useVideoPlayer
    }
  } = props;
  const [loading, setIsLoading] = useState(true);
  const [loadingText, setLoadingText] = useState('');
  const [isFailover, setIsFailover] = useState<boolean>(false);
  const [status, setStatus] = useState<string>('closed');
  const [mediaState, dispatch] = useReducer(mediaReducer, mediaShape);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isSupportGalleryView, setIsSupportGalleryView] = useState<boolean>(false);
  const zmClient = useContext(ZoomContext);
  const { slotBookingId, userId } = useParams<{ slotBookingId: string; userId: string }>();
  const [previewProps, setPreviewProps] = useState<any>({});
  const [isHost, setIsHost] = useState<boolean>(false);
  useEffect(() => {
    if (slotBookingId === 'meeting' && !zmClient.getSessionInfo()?.isInMeeting) {
      window.history.replaceState({}, '', window.location.pathname.replace('/meeting', ''));
      window.location.reload();
    }
  }, []);
  let webEndpoint: any;
  if (webEndpointArg) {
    webEndpoint = webEndpointArg;
  } else {
    webEndpoint = window?.webEndpoint ?? 'zoom.us';
  }
  const mediaContext = useMemo(() => ({ ...mediaState, mediaStream }), [mediaState, mediaStream]);
  const galleryViewWithoutSAB = Number(enforceGalleryView) === 1 && !window.crossOriginIsolated;
  const vbWithoutSAB = Number(enforceVB) === 1 && !window.crossOriginIsolated;
  const galleryViewWithAttach = Number(useVideoPlayer) === 1 && (window.crossOriginIsolated || galleryViewWithoutSAB);
  const [fullName, setFullName] = useState<string>('');

  const [speakerView, setSpeakerView] = useState(true);
  const [galleryView, setGalleryView] = useState(false);
  const [mainViewUser, setMainViewUser] = useState<Participant | null | undefined>(null);
  const [pinViewUser, setPinViewUser] = useState<Participant | null | undefined>(null);

  const viewContextValue = {
    speakerView,
    galleryView,
    mainViewUser,
    pinViewUser,
    setSpeakerView,
    setGalleryView,
    setMainViewUser,
    setPinViewUser
  };

  // if (galleryViewWithAttach) {
  //   console.log({
  //     galleryViewWithAttach,
  //     use: '<video-player-container> video tag render video',
  //     doc: 'https://marketplacefront.zoom.us/sdk/custom/web/modules/Stream.html#attachVideo'
  //   });
  // } else {
  //   console.log({
  //     galleryViewWithAttach,
  //     use: '<canvas>',
  //     doc: 'https://marketplacefront.zoom.us/sdk/custom/web/modules/Stream.html#startVideo'
  //   });
  // }

  useEffect(() => {
    const init = async () => {
      const libVersion = process.env.REACT_APP_ZOOM_LIB_VERSION;
      await zmClient.init('en-US', `${window.location.origin}/lib/${libVersion}`, {
        webEndpoint,
        enforceMultipleVideos: galleryViewWithoutSAB,
        enforceVirtualBackground: vbWithoutSAB,
        stayAwake: true,
        patchJsMedia: true,
        leaveOnPageUnload: true
      });
      try {
        // Get from env
        const admin_url = process.env.REACT_APP_ADMIN_URL;
        // Get SlotBookingId and UserID from url
        const endpoint = `${admin_url}/meetingZoom/getToken/${slotBookingId}/${userId}`;
        const options = {
          method: 'GET'
        };
        const response = await fetch(endpoint, options);
        const data = await response.json();
        let token = data.data.token;
        let name = data.data.fullName;
        setFullName(name);
        let topic = data.data.topic;
        let password = '';
        let subscriptionStatus = data.data.subscriptionStatus;
        let userRole = data.data.userRole;

        let a = '';
        setLoadingText('Joining the session...');
        setPreviewProps({
          topic,
          token,
          name,
          password,
          subscriptionStatus,
          userRole
        });
        const stream = zmClient.getMediaStream();
        setMediaStream(stream);
        setIsSupportGalleryView(stream.isSupportMultipleVideos());
        setIsLoading(false);
        setIsHost(zmClient.isHost());
      } catch (e: any) {
        setIsLoading(false);
        message.error(e.reason);
      }
    };
    init();
    return () => {
      if (zmClient.getSessionInfo()?.isInMeeting) {
        ZoomVideo.destroyClient();
      }
    };
  }, [
    sdkKey,
    signature,
    zmClient,
    topic,
    name,
    password,
    webEndpoint,
    galleryViewWithoutSAB,
    customerJoinId,
    lang,
    vbWithoutSAB
  ]);
  const onConnectionChange = useCallback(
    (payload: any) => {
      if (payload.state === ConnectionState.Reconnecting) {
        setIsLoading(true);
        setIsFailover(true);
        setStatus('connecting');
        const { reason, subsessionName } = payload;
        if (reason === ReconnectReason.Failover) {
          setLoadingText('Session Disconnected,Try to reconnect');
        } else if (reason === ReconnectReason.JoinSubsession || reason === ReconnectReason.MoveToSubsession) {
          setLoadingText(`Joining ${subsessionName}...`);
        } else if (reason === ReconnectReason.BackToMainSession) {
          setLoadingText('Returning to Main Session...');
        }
      } else if (payload.state === ConnectionState.Connected) {
        setStatus('connected');
        if (isFailover) {
          setIsLoading(false);
        }
        window.zmClient = zmClient;
        window.mediaStream = zmClient.getMediaStream();

        console.log('getSessionInfo', zmClient.getSessionInfo());
      } else if (payload.state === ConnectionState.Closed || payload.state === ConnectionState.Fail) {
        setStatus('closed');
        dispatch({ type: 'reset-media' });
        if (payload.state === ConnectionState.Fail) {
          Modal.error({
            title: 'Join meeting failed',
            content: `Join meeting failed. reason:${payload.reason ?? ''}`
          });
        }
        if (payload.reason === 'ended by host') {
          Modal.warning({
            title: 'Meeting ended',
            content: 'This meeting has been ended by host'
          });
        }
      }
    },
    [isFailover, zmClient]
  );
  const onMediaSDKChange = useCallback((payload: any) => {
    const { action, type, result } = payload;
    dispatch({ type: `${type}-${action}`, payload: result === 'success' });
  }, []);

  const onLeaveOrJoinSession = useCallback(async () => {
    if (status === 'closed') {
      setIsLoading(true);
      await zmClient.join(topic, signature, name, password);
      setIsLoading(false);
    } else if (status === 'connected') {
      await zmClient.leave();
      message.warn('You have left the session.');
    }
  }, [zmClient, status, topic, signature, name, password]);
  useEffect(() => {
    zmClient.on('connection-change', onConnectionChange);
    zmClient.on('media-sdk-change', onMediaSDKChange);
    return () => {
      zmClient.off('connection-change', onConnectionChange);
      zmClient.off('media-sdk-change', onMediaSDKChange);
    };
  }, [zmClient, onConnectionChange, onMediaSDKChange]);
  return (
    <div className="App">
      {loading && <LoadingLayer content={loadingText} />}
      {!loading && (
        <ViewContext.Provider value={viewContextValue}>
          <ZoomMediaContext.Provider value={mediaContext}>
            <Switch>
              <Route
                path="/Home"
                render={(props) => <Home {...props} status={status} onLeaveOrJoinSession={onLeaveOrJoinSession} />}
                exact
              />
              <Route path="/index.html" component={Home} exact />
              <Route path="/chat" component={Chat} />
              <Route path="/command" component={Command} />
              <Route
                path="/meeting/:slotBookingId/:userId"
                render={(props) => <Video {...props} userName={fullName} />}
              />

              <Route path="/subsession" component={Subsession} />
              <Route path="/leave/:slotBookingId/:userId" component={Leave} />
              {/* <Route path="/preview/:slotBookingId/:userId" component={Preview} /> */}
              <Route
                path="/:slotBookingId/:userId"
                render={(props) => <Preview {...props} previewProps={previewProps} />}
              />
            </Switch>
          </ZoomMediaContext.Provider>
        </ViewContext.Provider>
      )}
    </div>
  );
}

export default App;
